import { Check, ChevronDown, Loader2, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { MembershipRole, RoleOption } from "@/types";

/** Role dropdown (editable) or a static badge (read-only). */
export function RoleControl({
  role,
  roleOptions,
  editable,
  onChange,
  busy,
}: Readonly<{
  role: MembershipRole;
  roleOptions: RoleOption[];
  editable: boolean;
  onChange: (r: MembershipRole) => void;
  busy?: boolean;
}>) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const label = roleOptions.find((o) => o.value === role)?.label ?? role;

  // Close on click-outside / Escape so two rows' dropdowns can't stay open
  // and overlap each other.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!editable) {
    return <span className="text-xs text-content-tertiary shrink-0 px-2">{label}</span>;
  }
  return (
    <div className="relative shrink-0" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        disabled={busy}
        className="flex items-center gap-1 px-2 py-1 text-xs text-content-secondary hover:text-content-primary border border-border-secondary rounded-md transition disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : label}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-44 bg-surface-primary border border-border-secondary rounded-lg shadow-xl overflow-hidden">
          {roleOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                setOpen(false);
                if (opt.value !== role) onChange(opt.value);
              }}
              className="flex items-start gap-2 w-full px-3 py-2 text-left hover:bg-interactive-hover transition"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm text-content-primary">{opt.label}</div>
                <div className="text-xs text-content-quaternary">{opt.description}</div>
              </div>
              {opt.value === role && <Check className="w-4 h-4 text-brand-primary mt-0.5" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** A single member row (used for both user and team members). */
export function MemberRow({
  icon,
  title,
  subtitle,
  role,
  roleOptions,
  canManage,
  busy,
  onRoleChange,
  onRemove,
}: Readonly<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  role: MembershipRole;
  roleOptions: RoleOption[];
  canManage: boolean;
  busy: boolean;
  onRoleChange: (r: MembershipRole) => void;
  onRemove: () => void;
}>) {
  return (
    <div className="flex items-center gap-3 p-2.5">
      {icon}
      <div className="flex-1 min-w-0">
        <div className="text-sm text-content-primary font-medium truncate">{title}</div>
        <div className="text-xs text-content-quaternary truncate">{subtitle}</div>
      </div>
      <RoleControl role={role} roleOptions={roleOptions} editable={canManage} busy={busy} onChange={onRoleChange} />
      {canManage && (
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          className="p-1.5 hover:bg-red-500/20 text-content-tertiary hover:text-red-400 rounded-lg transition disabled:opacity-50"
          title="Remove"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
